import find from 'lodash/find';
import { useEffect, useRef, useState } from 'react';
import useSocket from './useSocket';
import { OTC_WEBSOCKET, P2P_WEBSOCKET } from '@/constants';
import numberUtils from '@/utils/numberUtils';

export type ResultType = {
  price: string;
  priceChange: string;
  total?: string;
  volume?: string;
  highPrice?: string;
  lowPrice?: string;
  universal?: string;
  buyPrice?: string;
  sellPrice?: string;
  midPrice?: string;
};

export type sourceType = 'p2p' | 'otc';

export type useLiveCryptoPropsType = {
  connect: boolean;
  pingPong: boolean;
  source: sourceType;
  symbol: string;
};

export type useLiveCryptoResultType = {
  isConnected: boolean;
  message?: ResultType;
};
let init = false;

const dataMap = {
  p2p: {
    price: 'c',
    priceChange: 'x',
    total: 'T',
    volume: 'V',
    highPrice: 'h',
    lowPrice: 'l',
    universal: 'u',
    buyPrice: '',
    sellPrice: '',
    midPrice: ''
  },
  otc: {
    buyPrice: 'B',
    sellPrice: 'S',
    price: 'L',
    priceChange: 'p',
    total: '',
    volume: '',
    highPrice: '',
    lowPrice: '',
    universal: '',
    midPrice: ['sellPrice', 'buyPrice']
  }
};

const handleFilter = (
  message: any,
  source: sourceType,
  symbol: string,
  subscribeMarket: string
): boolean => {
  const data = JSON.parse(message.data);
  if (source === 'p2p' && data?.stream === subscribeMarket) {
    return !!find(
      data?.data || [],
      item =>
        item.s.toString().toLowerCase() === symbol.toString().toLowerCase()
    );
  }
  if (source === 'otc') {
    return !!find(
      data || [],
      item =>
        item.s.toString().toLowerCase() === symbol.toString().toLowerCase()
    );
  }
  if (data === 2) return true;
  return false;
};

const useLiveCrypto = (
  props: useLiveCryptoPropsType
): useLiveCryptoResultType => {
  const subscribeMarket = '!all@ticker@rabex';

  const { source, symbol, connect = true, pingPong = true } = props;

  const SocketUrl = {
    p2p: P2P_WEBSOCKET,
    otc: OTC_WEBSOCKET
  };

  const [lastMessage, setLastMessage] = useState<ResultType | undefined>(
    undefined
  );
  const { isConnected, subscribe, lastJsonMessage }: any = useSocket(
    SocketUrl[source],
    {
      filter: message => handleFilter(message, source, symbol, subscribeMarket)
    },
    connect, // connect or not
    pingPong
  );

  const data = {
    p2p: lastJsonMessage?.data,
    otc: lastJsonMessage
  };

  useEffect(() => {
    if (isConnected && !init && source === 'p2p') {
      init = true;
      subscribe([subscribeMarket], 1);
    }
  }, [isConnected]);

  useEffect(() => {
    if (lastJsonMessage && lastJsonMessage !== '2') {
      const newData = find(data[source] || [], item => item.s === symbol);
      const newPriceChange = newData?.[dataMap[source]?.priceChange];
      if (
        !!newData &&
        Number(newPriceChange) !== Number(lastMessage?.priceChange || 0)
      ) {
        setLastMessage({
          priceChange: newPriceChange,
          price: `${numberUtils.delimiter(
            newData[dataMap[source]?.price] || 0
          )}`,
          total: newData[dataMap[source]?.total],
          volume: newData[dataMap[source]?.volume],
          highPrice: `${numberUtils.delimiter(
            newData[dataMap[source]?.highPrice] || 0
          )}`,
          lowPrice: `${numberUtils.delimiter(
            newData[dataMap[source]?.lowPrice] || 0
          )}`,
          universal: newData[dataMap[source]?.universal],
          buyPrice: `${numberUtils.delimiter(
            newData[dataMap[source]?.buyPrice] || 0
          )}`,
          sellPrice: `${numberUtils.delimiter(
            newData[dataMap[source]?.sellPrice]
          )}`,
          midPrice: `${numberUtils.delimiter(
            new numberUtils.bigDecimal(
              (Number(newData[dataMap[source]?.sellPrice]) +
                Number(newData[dataMap[source]?.buyPrice])) /
                2 || 0
            ).getValue()
          )}`
        });
      }
    }
  }, [lastJsonMessage]);

  return { message: lastMessage, isConnected };
};

export default useLiveCrypto;
